import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  RefreshCw, AlertTriangle, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, ExternalLink, Edit2, Trash2, Check,
  Send, Map, List, Activity, Search, Filter, Upload, X, Save, ShieldCheck, Zap,
} from "lucide-react";import { toast } from "sonner";
import AdminDataHealthMap from "./AdminDataHealthMap";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  success:      { bg: "bg-emerald-100", text: "text-emerald-700", dot: "#10b981", label: "Success" },
  partial:      { bg: "bg-amber-100",   text: "text-amber-700",   dot: "#f59e0b", label: "Partial"  },
  pending_ai:   { bg: "bg-amber-100",   text: "text-amber-700",   dot: "#f59e0b", label: "Partial"  },
  failed:       { bg: "bg-red-100",     text: "text-red-700",     dot: "#ef4444", label: "Failed"   },
  skipped:      { bg: "bg-slate-100",   text: "text-slate-500",   dot: "#94a3b8", label: "Skipped"  },
  not_attempted:{ bg: "bg-slate-100",   text: "text-slate-400",   dot: "#cbd5e1", label: "Not attempted" },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.not_attempted;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
      {s.label}
    </span>
  );
}

function StatCard({ label, value, sub, accent }) {
  const colors = { blue: "border-blue-500 bg-blue-50", amber: "border-amber-500 bg-amber-50",
    red: "border-red-500 bg-red-50", green: "border-green-500 bg-green-50",
    slate: "border-slate-300 bg-slate-50" };
  return (
    <div className={`rounded-xl border-l-4 p-4 ${colors[accent] || colors.slate}`} data-testid="stat-card">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1">{value ?? "—"}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Coach review mini-modal ────────────────────────────────────────────────────
function CoachReviewPanel({ collegeId, collegeName, adminToken, onClose }) {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/admin/data-health/college/${collegeId}/low-confidence-coaches`,
      { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(r => setCoaches(r.data))
      .catch(() => toast.error("Failed to load low-confidence coaches"))
      .finally(() => setLoading(false));
  }, [collegeId, adminToken]);

  const approve = async (id) => {
    await axios.patch(`${API}/api/admin/data-health/coaches/${id}/approve`, {},
      { headers: { Authorization: `Bearer ${adminToken}` } });
    setCoaches(c => c.filter(x => x.id !== id));
    toast.success("Coach approved");
  };

  const submitEdit = async (id) => {
    await axios.patch(`${API}/api/admin/data-health/coaches/${id}/edit`, editForm,
      { headers: { Authorization: `Bearer ${adminToken}` } });
    setCoaches(c => c.filter(x => x.id !== id));
    setEditing(null);
    toast.success("Coach edited and approved");
  };

  const discard = async (id) => {
    if (!window.confirm("Permanently delete this coach record?")) return;
    await axios.delete(`${API}/api/admin/data-health/coaches/${id}`,
      { headers: { Authorization: `Bearer ${adminToken}` } });
    setCoaches(c => c.filter(x => x.id !== id));
    toast.success("Coach discarded");
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-bold text-slate-900">Low-confidence coaches</h3>
            <p className="text-sm text-slate-500">{collegeName} — sub-70 confidence</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg font-bold">✕</button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {loading && <p className="text-sm text-slate-400 text-center py-8">Loading...</p>}
          {!loading && coaches.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-8">No sub-70 coaches found.</p>
          )}
          {coaches.map(c => (
            <div key={c.id} className="border rounded-xl p-4 space-y-2">
              {editing === c.id ? (
                <div className="space-y-2">
                  {["name","title","email"].map(f => (
                    <input key={f} placeholder={f.charAt(0).toUpperCase()+f.slice(1)}
                      value={editForm[f] ?? c[f] ?? ""}
                      onChange={e => setEditForm(p => ({...p, [f]: e.target.value}))}
                      className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  ))}
                  <select
                    value={editForm.role ?? c.role ?? "assistant_coach"}
                    onChange={e => setEditForm(p => ({...p, role: e.target.value}))}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="head_coach">Head Coach</option>
                    <option value="assistant_coach">Assistant Coach</option>
                  </select>
                  <div className="flex gap-2">
                    <button onClick={() => submitEdit(c.id)}
                      className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700">
                      Save &amp; Approve
                    </button>
                    <button onClick={() => setEditing(null)}
                      className="px-3 py-1.5 border text-xs rounded-lg hover:bg-slate-50">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{c.name || "—"}</p>
                      <p className="text-xs text-slate-500">{c.title || "No title"} · {c.email || "No email"}</p>
                      <p className="text-xs text-slate-400">{c.programme_gender} · conf {c.confidence_score}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                      {c.confidence_score}
                    </span>
                  </div>                  <div className="flex gap-2" data-testid="coach-review-actions">
                    <button onClick={() => approve(c.id)}
                      className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-lg border border-emerald-200 hover:bg-emerald-100">
                      <Check size={12} /> Approve
                    </button>
                    <button onClick={() => { setEditing(c.id); setEditForm({}); }}
                      className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-lg border border-blue-200 hover:bg-blue-100">
                      <Edit2 size={12} /> Edit
                    </button>
                    <button onClick={() => discard(c.id)}
                      className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 text-xs font-semibold rounded-lg border border-red-200 hover:bg-red-100">
                      <Trash2 size={12} /> Discard
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Per-school expanded row ────────────────────────────────────────────────────
function SchoolExpandedRow({ school, adminToken, onRescrape, onDismissFlag }) {
  const [history, setHistory] = useState(null);
  const [urlOverride, setUrlOverride] = useState(school.basketball_url || "");
  const [womensUrlOverride, setWomensUrlOverride] = useState(school.womens_basketball_url || "");
  const [apiEndpoint, setApiEndpoint] = useState(school.custom_api_endpoint || "");
  const [saving, setSaving] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/data-health/college/${school.id}/history`,
      { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(r => setHistory(r.data))
      .catch(() => setHistory([]));
  }, [school.id, adminToken]);

  const saveUrl = async (programme) => {
    setSaving(true);
    try {
      const url = programme === "womens" ? womensUrlOverride : urlOverride;
      await axios.patch(`${API}/api/admin/data-health/college/${school.id}/url`,
        { url, programme },
        { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success(`${programme === "womens" ? "Womens" : "Mens"} URL updated`);
    } catch { toast.error("Failed to update URL"); }
    finally { setSaving(false); }
  };

  const saveApiEndpoint = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/admin/data-health/college/${school.id}/api-endpoint`,
        { api_base_url: apiEndpoint },
        { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success("Custom API endpoint saved");
    } catch { toast.error("Failed to save — run migration v29 first?"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-slate-50 rounded-xl p-4 mt-2 space-y-4 border border-slate-200">
      {school.known_flag && (
        <div className="flex items-start justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle size={13} className="flex-shrink-0 mt-0.5 text-amber-600" />
            <span><strong>Known issue:</strong> {school.known_flag}</span>
          </div>
          <button
            onClick={() => onDismissFlag && onDismissFlag(school.id)}
            className="flex-shrink-0 px-2.5 py-1 bg-white border border-amber-300 hover:bg-amber-100 text-amber-700 font-semibold rounded-lg transition-colors whitespace-nowrap"
            data-testid={`dismiss-flag-${school.id}`}>
            Mark Reviewed
          </button>
        </div>
      )}

      {/* URL overrides */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Override mens basketball URL</label>
          <div className="flex gap-1">
            <input value={urlOverride} onChange={e => setUrlOverride(e.target.value)}
              placeholder="https://…/sports/mbball/coaches"
              className="flex-1 border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <button onClick={() => saveUrl("mens")} disabled={saving}
              className="px-2 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50">
              Save
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Override womens basketball URL</label>
          <div className="flex gap-1">
            <input value={womensUrlOverride} onChange={e => setWomensUrlOverride(e.target.value)}
              placeholder="https://…/sports/wbball/coaches"
              className="flex-1 border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <button onClick={() => saveUrl("womens")} disabled={saving}
              className="px-2 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50">
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Custom API endpoint */}
      <div>
        <label className="text-xs font-semibold text-slate-600 block mb-1">
          Try alternative API endpoint <span className="font-normal text-slate-400">(base URL — pipeline appends /api/v2/staff/coaches)</span>
        </label>
        <div className="flex gap-1">
          <input value={apiEndpoint} onChange={e => setApiEndpoint(e.target.value)}
            placeholder="https://und.com"
            className="flex-1 border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-purple-400" />
          <button onClick={saveApiEndpoint} disabled={saving}
            className="px-2 py-1.5 bg-purple-600 text-white text-xs font-bold rounded-lg hover:bg-purple-700 disabled:opacity-50">
            Save
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-0.5">
          After saving, use Re-scrape to trigger a new crawl using this endpoint.
        </p>
      </div>

      {/* Actions row */}
      <div className="flex flex-wrap gap-2 pt-1">
        <button onClick={() => onRescrape(school.id, school.name)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-xs font-bold rounded-lg hover:bg-slate-700 transition-colors"
          data-testid="rescrape-btn">
          <RefreshCw size={12} /> Re-scrape
        </button>
        {school.sub70_coaches > 0 && (
          <button onClick={() => setReviewOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700 transition-colors"
            data-testid="review-coaches-btn">
            <AlertTriangle size={12} /> Review {school.sub70_coaches} low-conf coaches
          </button>
        )}
        {school.basketball_url && (
          <a href={school.basketball_url} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 border text-xs font-semibold rounded-lg text-slate-600 hover:bg-slate-100">
            <ExternalLink size={12} /> Open page
          </a>
        )}
      </div>

      {/* Scrape history */}
      <div>
        <p className="text-xs font-semibold text-slate-600 mb-2">Scrape history (last 5)</p>
        {!history && <p className="text-xs text-slate-400">Loading…</p>}
        {history && history.length === 0 && <p className="text-xs text-slate-400">No history yet.</p>}
        {history && history.length > 0 && (
          <div className="space-y-1">
            {history.map((h, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                <StatusBadge status={h.status} />
                <span>{h.coaches_found ?? 0} coaches</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-400">{h.extraction_method || "—"}</span>
                <span className="text-slate-400 ml-auto">{new Date(h.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {reviewOpen && (
        <CoachReviewPanel
          collegeId={school.id}
          collegeName={school.name}
          adminToken={adminToken}
          onClose={() => setReviewOpen(false)}
        />
      )}
    </div>
  );
}

// ── Import Queue Section sub-component ────────────────────────────────────────
function ImportQueueSection({ title, accent, colleges, gender, promotingId, discardingId, onEdit, onPromote, onDiscard }) {
  const accentColors = {
    blue: "border-blue-400 text-blue-700 bg-blue-50",
    pink: "border-pink-400 text-pink-700 bg-pink-50",
  };
  const promoteColor = gender === "womens"
    ? "bg-pink-600 hover:bg-pink-700"
    : "bg-emerald-600 hover:bg-emerald-700";

  // Split into needs-work and already-promoted groups
  const needsWork    = colleges.filter(c => !c.has_live_womens);
  const alreadyLive  = colleges.filter(c => c.has_live_womens);

  const renderRow = (college) => {
    const primaryCoach  = (college.coaches || [])[0];
    const isPromoting   = promotingId === college.id;
    const isDiscarding  = discardingId === college.id;
    const busy          = isPromoting || isDiscarding;
    const isLive        = college.has_live_womens;
    const verified      = college.verified_count ?? 0;
    const total         = college.total_coach_count ?? (college.coaches || []).length;
    const allVerified   = total > 0 && verified === total;
    const someVerified  = verified > 0 && !allVerified;
    const noneVerified  = verified === 0;

    return (
      <tr
        key={college.id}
        className={`border-b last:border-0 transition-colors ${isLive ? "bg-green-50/40 opacity-70 hover:opacity-100" : "hover:bg-slate-50"}`}
        data-testid="import-queue-row"
      >
        <td className="px-4 py-3 font-semibold text-slate-900 max-w-[200px]">
          <div className="flex items-center gap-2 truncate">
            <span className="truncate">{college.name}</span>
            {isLive && (
              <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
                <CheckCircle size={9} /> Live
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-slate-500">{college.division || "—"}</td>
        <td className="px-4 py-3 text-slate-500">{college.state || "—"}</td>
        <td className="px-4 py-3 text-slate-600 max-w-[180px]">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="truncate">
              {primaryCoach?.name
                ? <><span className="truncate">{primaryCoach.name}</span>{primaryCoach?.title && <div className="text-slate-400 truncate text-[10px]">{primaryCoach.title}</div>}</>
                : <span className="text-slate-300 italic">— empty —</span>}
            </div>
            {/* Verification progress pill */}
            {total > 0 && (
              <span className={`flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${
                allVerified  ? "bg-green-100 text-green-700" :
                someVerified ? "bg-amber-100 text-amber-700" :
                               "bg-slate-100 text-slate-500"
              }`}>
                {verified}/{total} verified
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-slate-500 max-w-[180px]">
          <div className="truncate">{primaryCoach?.email || "—"}</div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-1.5">
            <button onClick={() => onEdit(college)} disabled={busy}
              className="flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40"
              data-testid={`edit-import-btn-${gender}`} title="Edit">
              <Edit2 size={11} /> Edit
            </button>
            {!isLive && (
              <button onClick={() => onPromote(college)} disabled={busy}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-white rounded-lg transition-colors disabled:opacity-40 ${promoteColor}`}
                data-testid={`promote-single-btn-${gender}`} title="Go Live">
                {isPromoting ? <RefreshCw size={11} className="animate-spin" /> : <CheckCircle size={11} />}
                {isPromoting ? "…" : "Go Live"}
              </button>
            )}
            <button onClick={() => onDiscard(college)} disabled={busy}
              className="flex items-center gap-1 px-2 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
              data-testid={`discard-import-btn-${gender}`} title="Discard">
              {isDiscarding ? <RefreshCw size={11} className="animate-spin" /> : <Trash2 size={11} />}
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-2">
      <div className={`flex items-center gap-2 px-3 py-1.5 border-l-4 rounded-r-lg text-xs font-bold uppercase tracking-wide ${accentColors[accent]}`}>
        {title}
        <span className="font-normal opacity-60">— {needsWork.length} pending{alreadyLive.length > 0 ? `, ${alreadyLive.length} live` : ""}</span>
      </div>
      {colleges.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-xs bg-slate-50 rounded-xl border border-dashed">
          {gender === "womens"
            ? 'No women\'s entries yet — click "Copy Live → Women\'s" to populate'
            : "No pending men's imports"}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border overflow-x-auto">
          <table className="w-full text-xs" data-testid={`import-queue-table-${gender}`}>
            <thead>
              <tr className="border-b bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
                <th className="text-left px-4 py-3 font-semibold">College</th>
                <th className="text-left px-4 py-3 font-semibold">Division</th>
                <th className="text-left px-4 py-3 font-semibold">State</th>
                <th className="text-left px-4 py-3 font-semibold">Coaches</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {needsWork.map(renderRow)}
              {alreadyLive.length > 0 && needsWork.length > 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-2 bg-green-50 border-t border-green-100">
                    <span className="text-[10px] font-bold text-green-600 uppercase tracking-wide flex items-center gap-1">
                      <CheckCircle size={9} /> Already promoted to live — {alreadyLive.length} college{alreadyLive.length !== 1 ? "s" : ""}
                    </span>
                  </td>
                </tr>
              )}
              {alreadyLive.map(renderRow)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function AdminDataHealth({ adminToken }) {
  const [activeTab, setActiveTab]   = useState("overview");
  const [stats, setStats]           = useState(null);
  const [queue, setQueue]           = useState([]);
  const [queueFilter, setQueueFilter] = useState("all");
  const [search, setSearch]         = useState("");
  const [expanded, setExpanded]     = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [promotePreview, setPromotePreview] = useState(null);
  const [promoting, setPromoting]   = useState(false);
  const [promoteResult, setPromoteResult] = useState(null);
  const [showPromoteModal, setShowPromoteModal] = useState(false);

  // Import Queue state
  const [importQueue, setImportQueue]       = useState({ mens: [], womens: [] });
  const [loadingImport, setLoadingImport]   = useState(false);
  const [editingCollege, setEditingCollege] = useState(null);
  const [editGender, setEditGender]         = useState("mens"); // which section opened the modal
  const [editForm, setEditForm]             = useState({});
  const [savingEdit, setSavingEdit]         = useState(false);
  const [promotingId, setPromotingId]       = useState(null);
  const [discardingId, setDiscardingId]     = useState(null);
  const [copyingToWomens, setCopyingToWomens] = useState(false);

  // Bulk Verify state
  const [bvPreview, setBvPreview]         = useState(null);   // { total, coaches }
  const [bvLoading, setBvLoading]         = useState(false);
  const [bvRunning, setBvRunning]         = useState(null);   // collegeId | 'all'
  const [bvResult, setBvResult]           = useState(null);
  const [bvSearch, setBvSearch]           = useState("");
  const [bvCollegeFilter, setBvCollegeFilter] = useState("all"); // 'all' | collegeId

  // Outreach fast-track state
  const [ftPreview, setFtPreview]         = useState(null);   // { total, coaches }
  const [ftLoading, setFtLoading]         = useState(false);
  const [ftRunning, setFtRunning]         = useState(false);
  const [ftResult, setFtResult]           = useState(null);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const [statsRes, previewRes] = await Promise.all([
        axios.get(`${API}/api/admin/data-health/stats`, { headers }),
        axios.get(`${API}/api/admin/data-health/promote-preview`, { headers }),
      ]);
      setStats(statsRes.data);
      setPromotePreview(previewRes.data);
    } catch { toast.error("Failed to load data health stats"); }
    finally { setLoadingStats(false); }
  }, [adminToken]);

  const fetchQueue = useCallback(async (filter = queueFilter) => {
    setLoadingQueue(true);
    try {
      const params = filter !== "all" ? { status_filter: filter } : {};
      const r = await axios.get(`${API}/api/admin/data-health/review-queue`, { headers, params });
      setQueue(r.data);
    } catch { toast.error("Failed to load review queue"); }
    finally { setLoadingQueue(false); }
  }, [adminToken, queueFilter]);

  const fetchImportQueue = useCallback(async () => {
    setLoadingImport(true);
    try {
      const r = await axios.get(`${API}/api/admin/data-health/import-queue`, { headers });
      setImportQueue(r.data);
    } catch { toast.error("Failed to load import queue"); }
    finally { setLoadingImport(false); }
  }, [adminToken]);
  useEffect(() => { fetchStats(); fetchImportQueue(); }, [fetchStats, fetchImportQueue]);
  useEffect(() => {
    if (activeTab === "queue") fetchQueue();
    if (activeTab === "imports") fetchImportQueue();
  }, [activeTab, fetchQueue, fetchImportQueue]);

  const handleRescrape = async (id, name) => {
    try {
      await axios.post(`${API}/api/admin/data-health/college/${id}/rescrape`, {}, { headers });
      toast.success(`Re-scrape queued for ${name}`);
    } catch { toast.error("Failed to queue re-scrape"); }
  };

  const handleDismissFlag = async (id) => {
    try {
      await axios.post(`${API}/api/admin/data-health/college/${id}/dismiss-flag`, {}, { headers });
      toast.success("Flag cleared — school marked as reviewed");
      // Remove the flag from local state immediately (optimistic UI)
      setQueue(prev => prev.map(s => s.id === id ? { ...s, known_flag: null } : s));
    } catch { toast.error("Failed to clear flag"); }
  };

  const handleDigest = async () => {
    setSendingDigest(true);
    try {
      await axios.post(`${API}/api/admin/data-health/digest/send`, {}, { headers });
      toast.success("Weekly digest sent to admin inbox");
    } catch { toast.error("Failed to send digest"); }
    finally { setSendingDigest(false); }
  };

  const handlePromote = async () => {
    setPromoting(true);
    setPromoteResult(null);
    try {
      const r = await axios.post(`${API}/api/admin/data-health/promote-to-live`, {}, { headers });
      setPromoteResult(r.data);
      setShowPromoteModal(false);
      toast.success(`${r.data.promoted} coaches promoted to live table`);
      fetchStats(); // refresh preview count
    } catch { toast.error("Promote to live failed"); }
    finally { setPromoting(false); }
  };

  const fetchBulkVerifyPreview = useCallback(async () => {
    setBvLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/scraper/bulk-verify/preview`, { headers });
      setBvPreview(r.data);
    } catch { toast.error("Failed to load bulk verify preview"); }
    finally { setBvLoading(false); }
  }, [adminToken]);

  const runBulkVerify = async (collegeId = null) => {
    const scopeLabel = collegeId
      ? (bvPreview?.coaches || []).find(c => c.college_id === collegeId)?.college_name || "selected college"
      : "ALL eligible coaches";
    const count = collegeId
      ? (bvPreview?.coaches || []).filter(c => c.college_id === collegeId).length
      : bvPreview?.total || 0;
    if (!window.confirm(
      `Bulk verify ${count} coach${count !== 1 ? "es" : ""} at ${scopeLabel}?\n\n` +
      `Only confidence >= 70 coaches with human_verified=FALSE will be updated.\n` +
      `Already-verified coaches are excluded at the query level — their data will NOT change.\n` +
      `Sets verification_method="bulk_auto". Cannot be undone in bulk.`
    )) return;

    setBvRunning(collegeId || "all");
    setBvResult(null);
    try {
      const payload = { verified_by: "admin" };
      if (collegeId) payload.college_id = collegeId;
      const r = await axios.post(`${API}/api/admin/scraper/bulk-verify`, payload, { headers });
      setBvResult(r.data);
      toast.success(`${r.data.verified_count} coach${r.data.verified_count !== 1 ? "es" : ""} verified`);
      fetchBulkVerifyPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk verify failed");
    } finally { setBvRunning(null); }
  };

  useEffect(() => {
    if (activeTab === "bulkverify") fetchBulkVerifyPreview();
    if (activeTab === "outreach")   fetchFastTrackPreview();
  }, [activeTab, fetchBulkVerifyPreview]);

  const fetchFastTrackPreview = useCallback(async () => {
    setFtLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/scraper/outreach-fasttrack/preview`, { headers });
      setFtPreview(r.data);
    } catch { toast.error("Failed to load fast-track queue"); }
    finally { setFtLoading(false); }
  }, [adminToken]);

  const runFastTrackVerify = async () => {
    setFtRunning(true);
    setFtResult(null);
    try {
      const r = await axios.post(`${API}/api/admin/scraper/outreach-fasttrack/verify`,
        { verified_by: "admin" }, { headers });
      setFtResult(r.data);
      setFtPreview(null);
      toast.success(`Fast-track complete: ${r.data.promoted} promoted, ${r.data.updated} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fast-track verify failed");
    } finally { setFtRunning(false); }
  };

  const filteredQueue = queue.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.state || "").toLowerCase().includes(search.toLowerCase())
  );

  // Import queue handlers
  const openEdit = (college, gender) => {
    const coachesOfGender = (college.coaches || []).filter(c =>
      gender === "womens" ? c.programme_gender === "womens" : c.programme_gender !== "womens"
    );
    setEditGender(gender);
    setEditingCollege(college);
    setEditForm({
      name:             college.name || "",
      location:         college.location || "",
      state:            college.state || "",
      division:         college.division || "",
      conference:       college.conference || "",
      region:           college.region || "",
      acceptance_rate:  college.acceptance_rate || "",
      ranking:          college.ranking || "",
      website:          college.website || "",
      image_url:        college.image_url || "",
      notable_alumni:   college.notable_alumni || "",
      scholarship_info: college.scholarship_info || "",
      scholarship_type: college.scholarship_type || "",
      language_of_study:college.language_of_study || "",
      foreign_friendly: college.foreign_friendly ? "yes" : "no",
      coaches: coachesOfGender.map(c => ({
        id:            c.id,
        name:          c.name || "",
        title:         c.title || "",
        email:         c.email || "",
        phone:         c.phone || "",
        last_verified: c.last_verified || "",
      })),
    });
  };

  const saveEdit = async (thenPromote = false) => {
    if (!editingCollege) return;
    const college = editingCollege;
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        foreign_friendly: editForm.foreign_friendly === "yes",
        coaches: editForm.coaches || [],
        coach_programme_gender: editGender,
      };
      await axios.patch(
        `${API}/api/admin/data-health/import-queue/${college.id}`,
        payload,
        { headers }
      );
      toast.success("Changes saved");
      setEditingCollege(null);
      if (thenPromote) await promoteSingle(college, editGender);
      else fetchImportQueue();
    } catch { toast.error("Save failed"); }
    finally { setSavingEdit(false); }
  };

  const promoteSingle = async (college, gender = "mens") => {
    setPromotingId(college.id);
    try {
      const r = await axios.post(
        `${API}/api/admin/data-health/import-queue/${college.id}/promote?gender=${gender}`,
        {},
        { headers }
      );
      toast.success(r.data.message);
      fetchImportQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally { setPromotingId(null); }
  };

  const discardSingle = async (college, gender) => {
    if (!window.confirm(`Discard "${college.name}"? This cannot be undone.`)) return;
    setDiscardingId(college.id);
    try {
      await axios.delete(
        `${API}/api/admin/data-health/import-queue/${college.id}?gender=${gender}`,
        { headers }
      );
      toast.success(`"${college.name}" discarded`);
      fetchImportQueue();
    } catch { toast.error("Discard failed"); }
    finally { setDiscardingId(null); }
  };

  const copyLiveToWomens = async () => {
    setCopyingToWomens(true);
    try {
      const r = await axios.post(`${API}/api/admin/data-health/import-queue/copy-live-to-womens`, {}, { headers });
      toast.success(r.data.message);
      fetchImportQueue();
    } catch { toast.error("Copy failed"); }
    finally { setCopyingToWomens(false); }
  };

  const CONF_COLORS = ["#10b981","#3b82f6","#f59e0b","#ef4444"];

  return (
    <div className="space-y-6" data-testid="data-health-panel">
      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b pb-1">
        {[
          { id: "overview",    label: "Overview",        Icon: Activity    },
          { id: "queue",       label: "Review Queue",    Icon: List        },
          { id: "imports",     label: "Import Queue",    Icon: Upload      },
          { id: "bulkverify",  label: "Bulk Verify",     Icon: ShieldCheck },
          { id: "outreach",    label: "Fast-Track",      Icon: Zap         },
          { id: "map",         label: "Coverage Map",    Icon: Map         },
        ].map(({ id, label, Icon }) => (
          <button key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all
              ${activeTab === id ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`dh-tab-${id}`}>
            <Icon size={13} /> {label}
            {id === "imports" && (importQueue.mens.length + importQueue.womens.length) > 0 && (
              <span className="ml-1 bg-orange-500 text-white rounded-full text-[10px] font-bold px-1.5 py-0.5 leading-none">
                {importQueue.mens.length + importQueue.womens.length}
              </span>
            )}
            {id === "bulkverify" && bvPreview && bvPreview.total > 0 && (
              <span className="ml-1 bg-blue-500 text-white rounded-full text-[10px] font-bold px-1.5 py-0.5 leading-none">
                {bvPreview.total}
              </span>
            )}
            {id === "outreach" && ftPreview && ftPreview.total > 0 && (
              <span className="ml-1 bg-amber-500 text-white rounded-full text-[10px] font-bold px-1.5 py-0.5 leading-none">
                {ftPreview.total}
              </span>
            )}
          </button>
        ))}
        <div className="ml-auto">
          <button onClick={handleDigest} disabled={sendingDigest}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            data-testid="send-digest-btn">
            <Send size={12} /> {sendingDigest ? "Sending…" : "Send digest"}
          </button>
        </div>
      </div>

      {/* ── Overview tab ────────────────────────────────────────────── */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {loadingStats ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading stats…</div>
          ) : stats ? (
            <>
              {/* Promote to Live banner */}
              {promotePreview && promotePreview.total > 0 && (
                <div className="flex items-center justify-between gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
                  <div>
                    <p className="text-sm font-bold text-emerald-800">
                      {promotePreview.total} verified coach{promotePreview.total !== 1 ? "es" : ""} ready to promote
                    </p>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      {promotePreview.mens}M / {promotePreview.womens}W — human_verified = true in staging
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPromoteModal(true)}
                    className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors"
                    data-testid="promote-to-live-btn">
                    <CheckCircle size={14} /> Promote to Live
                  </button>
                </div>
              )}
              {promotePreview && promotePreview.total === 0 && (
                <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs text-slate-500">
                  <CheckCircle size={14} className="text-slate-400" />
                  No verified coaches pending promotion. Approve coaches in the Review Queue first.
                </div>
              )}
              {promoteResult && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-2xl text-xs text-blue-800 font-semibold">
                  <CheckCircle size={14} className="text-blue-600" />
                  {promoteResult.message}
                </div>
              )}
              {/* KPI cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Total Coaches" value={stats.total_coaches}
                  sub={`${stats.mens_coaches}M / ${stats.womens_coaches}W`} accent="blue" />
                <StatCard label="Schools Scraped" value={`${stats.school_success}/${stats.school_total}`}
                  sub={`${Math.round(stats.school_success*100/Math.max(stats.school_total,1))}% success`} accent="green" />
                <StatCard label="Errors (24h)" value={stats.errors_24h}
                  sub={`${stats.success_24h} successes · ${stats.coaches_found_24h} coaches found`} accent={stats.errors_24h > 5 ? "red" : "slate"} />
                <StatCard label="Needs Review" value={stats.sub70_coaches}
                  sub="sub-70 confidence coaches" accent={stats.sub70_coaches > 20 ? "amber" : "slate"} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatCard label="Partial Schools" value={stats.school_partial} sub="fetched but 0 coaches" accent="amber" />
                <StatCard label="Failed / Skipped" value={stats.school_failed} sub="no data extracted" accent="red" />
                <StatCard label="Last Run" value={stats.last_run ? new Date(stats.last_run).toLocaleDateString() : "—"}
                  sub={stats.last_run ? new Date(stats.last_run).toLocaleTimeString() : "no runs yet"} accent="slate" />
              </div>

              {/* Confidence distribution */}
              <div className="bg-white rounded-2xl border p-5">
                <h3 className="font-bold text-slate-900 mb-4 text-sm">Confidence Distribution</h3>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={stats.confidence_distribution} barSize={48}>
                    <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [v, "Coaches"]} />
                    <Bar dataKey="count" radius={[4,4,0,0]}>
                      {stats.confidence_distribution.map((_, i) => (
                        <Cell key={i} fill={CONF_COLORS[i]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Recent job history */}
              <div className="bg-white rounded-2xl border p-5">
                <h3 className="font-bold text-slate-900 mb-3 text-sm">Recent Scrape Activity</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-slate-400 uppercase tracking-wide">
                        <th className="text-left py-2 font-semibold">Date</th>
                        <th className="text-left py-2 font-semibold">Status</th>
                        <th className="text-right py-2 font-semibold">Coaches</th>
                        <th className="text-left py-2 pl-3 font-semibold">Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent_jobs.map((j, i) => (
                        <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="py-2 text-slate-600">{new Date(j.created_at).toLocaleString()}</td>
                          <td className="py-2"><StatusBadge status={j.status} /></td>
                          <td className="py-2 text-right font-semibold">{j.coaches_found}</td>
                          <td className="py-2 pl-3 text-slate-400">{j.extraction_method || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* ── Review Queue tab ────────────────────────────────────────── */}
      {activeTab === "queue" && (
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-1 border rounded-lg overflow-hidden text-xs">
              {["all","partial","failed","flagged"].map(f => (
                <button key={f}
                  onClick={() => { setQueueFilter(f); fetchQueue(f); }}
                  className={`px-3 py-2 font-semibold capitalize transition-colors
                    ${queueFilter === f ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
                  {f === "flagged" ? "⚠ Flagged" : f.charAt(0).toUpperCase()+f.slice(1)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2 text-xs bg-white flex-1 max-w-xs">
              <Search size={13} className="text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search schools…"
                className="outline-none flex-1 text-slate-700 placeholder-slate-400" />
            </div>
            <span className="text-xs text-slate-400 ml-auto">{filteredQueue.length} schools</span>
          </div>

          {/* Queue table */}
          {loadingQueue ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading review queue…</div>
          ) : filteredQueue.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No schools match this filter.</div>
          ) : (
            <div className="space-y-2">
              {filteredQueue.map(school => (
                <div key={school.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  {/* Row header */}
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
                    onClick={() => setExpanded(expanded === school.id ? null : school.id)}
                    data-testid="queue-row">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold text-slate-900 text-sm">{school.name}</span>
                      {school.state && (
                        <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{school.state}</span>
                      )}
                      <StatusBadge status={school.last_status} />
                      {school.known_flag && (
                        <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                          <AlertTriangle size={10} /> Known issue
                        </span>
                      )}
                      <span className="ml-auto text-xs text-slate-400 flex items-center gap-2">
                        {school.coaches_in_staging > 0 && (
                          <span>{school.coaches_in_staging} coaches
                            {school.sub70_coaches > 0 && (
                              <span className="ml-1 text-amber-600 font-semibold">({school.sub70_coaches} &lt;70)</span>
                            )}
                          </span>
                        )}
                        {school.last_scraped_at && (
                          <span className="text-slate-300">·</span>
                        )}
                        {school.last_scraped_at && (
                          <span>{new Date(school.last_scraped_at).toLocaleDateString()}</span>
                        )}
                        {expanded === school.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </span>
                    </div>
                    {school.basketball_url && (
                      <p className="text-xs text-slate-400 mt-1 truncate max-w-xl">{school.basketball_url}</p>
                    )}
                  </button>

                  {/* Expanded controls */}
                  {expanded === school.id && (
                    <div className="px-4 pb-4">
                      <SchoolExpandedRow
                        school={school}
                        adminToken={adminToken}
                        onRescrape={handleRescrape}
                        onDismissFlag={handleDismissFlag}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Bulk Verify tab ─────────────────────────────────────────────── */}
      {activeTab === "bulkverify" && (
        <div className="space-y-5" data-testid="bulk-verify-panel">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-slate-900 text-sm">
                Bulk Verify — Confidence ≥ 70, Not Yet Human-Verified
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                These coaches are live, passed the confidence gate (≥ 70), but lack a manual sign-off.
                Bulk verify marks them <code className="bg-slate-100 px-1 rounded">human_verified=TRUE</code> and
                records <code className="bg-slate-100 px-1 rounded">verification_method="bulk_auto"</code>.
                Already-verified coaches are <strong>excluded at the query level</strong> — their data is never touched.
              </p>
            </div>
            <button onClick={fetchBulkVerifyPreview} disabled={bvLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-50 disabled:opacity-50"
              data-testid="bv-refresh-btn">
              <RefreshCw size={12} className={bvLoading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>

          {bvResult && (
            <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-semibold"
              data-testid="bv-result-banner">
              <CheckCircle size={14} className="text-emerald-600 flex-shrink-0" />
              <span>
                {bvResult.verified_count} coach{bvResult.verified_count !== 1 ? "es" : ""} verified —
                method: bulk_auto · by: {bvResult.verified_by} · at: {new Date(bvResult.verified_at).toLocaleString()}
              </span>
            </div>
          )}

          {bvLoading ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading preview…</div>
          ) : bvPreview ? (
            <>
              {bvPreview.total === 0 ? (
                <div className="flex items-center gap-3 p-5 bg-emerald-50 border border-emerald-200 rounded-2xl" data-testid="bv-empty-state">
                  <CheckCircle size={18} className="text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-emerald-800">All caught up</p>
                    <p className="text-xs text-emerald-600">No coaches in the live table with confidence ≥ 70 and human_verified=FALSE.</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-xs">
                      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        className="w-full pl-8 pr-3 py-2 border rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-slate-400"
                        placeholder="Search coach or college…"
                        value={bvSearch}
                        onChange={e => setBvSearch(e.target.value)}
                        data-testid="bv-search-input"
                      />
                    </div>
                    <span className="text-xs text-slate-500 font-semibold">{bvPreview.total} eligible</span>
                    <button
                      onClick={() => runBulkVerify(null)}
                      disabled={!!bvRunning}
                      className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition-colors disabled:opacity-50"
                      data-testid="bv-verify-all-btn">
                      <ShieldCheck size={14} />
                      {bvRunning === "all" ? "Verifying…" : `Verify All ${bvPreview.total}`}
                    </button>
                  </div>

                  {(() => {
                    const groups = {};
                    (bvPreview.coaches || []).forEach(c => {
                      const cid = c.college_id;
                      if (!groups[cid]) groups[cid] = { name: c.college_name || cid, coaches: [] };
                      groups[cid].coaches.push(c);
                    });
                    const search = bvSearch.toLowerCase();
                    return Object.entries(groups)
                      .filter(([, g]) => !search ||
                        g.name.toLowerCase().includes(search) ||
                        g.coaches.some(c => (c.name || "").toLowerCase().includes(search) || (c.email || "").toLowerCase().includes(search))
                      )
                      .sort((a, b) => a[1].name.localeCompare(b[1].name))
                      .map(([cid, group]) => (
                        <div key={cid} className="bg-white rounded-2xl border overflow-hidden" data-testid={`bv-college-${cid}`}>
                          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
                            <div>
                              <span className="text-xs font-bold text-slate-800">{group.name}</span>
                              <span className="ml-2 text-[10px] text-slate-400">{group.coaches.length} coach{group.coaches.length !== 1 ? "es" : ""}</span>
                            </div>
                            <button onClick={() => runBulkVerify(cid)} disabled={!!bvRunning}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-[11px] font-bold rounded-lg transition-colors disabled:opacity-40"
                              data-testid={`bv-college-verify-btn-${cid}`}>
                              <ShieldCheck size={12} />
                              {bvRunning === cid ? "Verifying…" : `Verify ${group.coaches.length}`}
                            </button>
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-[10px] text-slate-400 uppercase tracking-wide border-b bg-slate-50/50">
                                <th className="px-4 py-2 text-left font-semibold w-1/4">Name</th>
                                <th className="px-4 py-2 text-left font-semibold w-1/3">Email</th>
                                <th className="px-4 py-2 text-left font-semibold">Title</th>
                                <th className="px-4 py-2 text-right font-semibold">Conf</th>
                                <th className="px-4 py-2 text-right font-semibold">Gender</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.coaches.map(c => (
                                <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`bv-coach-row-${c.id}`}>
                                  <td className="px-4 py-2.5 font-medium text-slate-800">
                                    {c.name || (`${c.first_name || ""} ${c.last_name || ""}`.trim()) || "(no name)"}
                                  </td>
                                  <td className="px-4 py-2.5 text-slate-500">{c.email || "—"}</td>
                                  <td className="px-4 py-2.5 text-slate-500 max-w-[200px] truncate">{c.title || "—"}</td>
                                  <td className="px-4 py-2.5 text-right font-bold text-emerald-700">{c.confidence_score}</td>
                                  <td className="px-4 py-2.5 text-right text-slate-400">{c.programme_gender || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ));
                  })()}
                </>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-slate-400 text-sm">
              Click Refresh to load the bulk verify preview.
            </div>
          )}
        </div>
      )}

      {/* ── Coverage Map tab ─────────────────────────────────────────── */}
      {activeTab === "map" && (
        <AdminDataHealthMap adminToken={adminToken} />
      )}

      {/* ── Outreach Fast-Track tab ───────────────────────────────────── */}
      {activeTab === "outreach" && (
        <div className="space-y-5" data-testid="outreach-fasttrack-panel">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-slate-900">Outreach Fast-Track</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Staging men's coaches whose email + college exactly matches a sent email in the outreach history.
                No confidence score floor — confirmed send history is the verification signal.
                Promotes to live with <code className="bg-slate-100 px-1 rounded text-[11px]">verification_method='outreach_confirmed'</code>.
              </p>
            </div>
            <button onClick={fetchFastTrackPreview} disabled={ftLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              data-testid="ft-refresh-btn">
              <RefreshCw size={13} className={ftLoading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>

          {ftResult && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm font-semibold text-emerald-800" data-testid="ft-result-banner">
              Done — {ftResult.promoted} promoted to live · {ftResult.updated} updated in place · {ftResult.staging_removed} staging rows removed
            </div>
          )}

          {ftLoading ? (
            <div className="text-center py-10 text-slate-400 text-sm">Loading fast-track queue…</div>
          ) : !ftPreview ? (
            <div className="text-center py-10 text-slate-400 text-sm">Click Refresh to load the queue.</div>
          ) : ftPreview.total === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">
              Queue is empty — no staging men's coaches with matching outreach history found.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">
                  {ftPreview.total} coaches ready for fast-track promotion
                </span>
                <button onClick={runFastTrackVerify} disabled={ftRunning}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-colors"
                  data-testid="ft-verify-all-btn">
                  <Zap size={13} />
                  {ftRunning ? "Promoting…" : `Promote all ${ftPreview.total} to live`}
                </button>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wide">Coach</th>
                      <th className="px-3 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wide">Email</th>
                      <th className="px-3 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wide">College</th>
                      <th className="px-3 py-2.5 text-center font-bold text-slate-500 uppercase tracking-wide">Sends</th>
                      <th className="px-3 py-2.5 text-center font-bold text-slate-500 uppercase tracking-wide">Conf</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ftPreview.coaches.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-50" data-testid={`ft-row-${c.id}`}>
                        <td className="px-3 py-2.5 font-medium text-slate-800">{c.name || <span className="text-slate-400 italic">no name</span>}</td>
                        <td className="px-3 py-2.5 text-slate-500">{c.email}</td>
                        <td className="px-3 py-2.5 text-slate-600">{c.college_name}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="inline-block bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full">{c.send_count}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center text-slate-400">{c.confidence_score ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Import Queue tab ──────────────────────────────────────────── */}
      {activeTab === "imports" && (
        <div className="space-y-6" data-testid="import-queue-panel">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-bold text-slate-900 text-sm">CSV Import Queue</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Edit details then promote individually. Men's = new colleges; Women's = all live colleges.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={copyLiveToWomens} disabled={copyingToWomens}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-colors"
                data-testid="copy-live-to-womens-btn">
                {copyingToWomens ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
                {copyingToWomens ? "Copying…" : "Copy Live → Women's"}
              </button>
              <button onClick={fetchImportQueue}
                className="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs text-slate-500 hover:bg-slate-50"
                data-testid="refresh-import-queue-btn">
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
          </div>

          {loadingImport ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
          ) : (
            <>
              {/* ── Men's Program ── */}
              <ImportQueueSection
                title="Men's Program"
                accent="blue"
                colleges={importQueue.mens}
                gender="mens"
                promotingId={promotingId}
                discardingId={discardingId}
                onEdit={(col) => openEdit(col, "mens")}
                onPromote={(col) => promoteSingle(col, "mens")}
                onDiscard={(col) => discardSingle(col, "mens")}
              />
              {/* ── Women's Program ── */}
              <ImportQueueSection
                title="Women's Program"
                accent="pink"
                colleges={importQueue.womens}
                gender="womens"
                promotingId={promotingId}
                discardingId={discardingId}
                onEdit={(col) => openEdit(col, "womens")}
                onPromote={(col) => promoteSingle(col, "womens")}
                onDiscard={(col) => discardSingle(col, "womens")}
              />
            </>
          )}
        </div>
      )}

      {/* ── Edit Import College modal — matches live college edit exactly ─── */}
      {editingCollege && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="edit-import-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="font-black text-slate-900 text-lg uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                Edit College
                <span className={`ml-2 text-sm font-semibold px-2 py-0.5 rounded-full ${editGender === "womens" ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"}`}>
                  {editGender === "womens" ? "Women's" : "Men's"}
                </span>
              </h3>
              <button onClick={() => setEditingCollege(null)} className="text-slate-400 hover:text-slate-700">
                <XCircle size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Basic fields — exact same order and labels as live modal */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: "name",            label: "College Name",          full: true },
                  { key: "location",        label: "Location (City, State)"             },
                  { key: "state",           label: "State"                              },
                  { key: "division",        label: "Division"                           },
                  { key: "conference",      label: "Conference"                         },
                  { key: "region",          label: "Region"                             },
                  { key: "acceptance_rate", label: "Acceptance Rate"                    },
                  { key: "ranking",         label: "Ranking #"                          },
                  { key: "website",         label: "Website URL"                        },
                  { key: "image_url",       label: "Image URL",             full: true  },
                ].map(f => (
                  <div key={f.key} className={f.full ? "col-span-2" : ""}>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">{f.label}</label>
                    <input type="text" value={editForm[f.key] ?? ""}
                      onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                      data-testid={`edit-field-${f.key}`} />
                  </div>
                ))}
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Notable Alumni</label>
                  <input type="text" value={editForm.notable_alumni ?? ""}
                    onChange={e => setEditForm(p => ({ ...p, notable_alumni: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                    data-testid="edit-field-notable_alumni" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Scholarship Info</label>
                  <textarea rows={2} value={editForm.scholarship_info ?? ""}
                    onChange={e => setEditForm(p => ({ ...p, scholarship_info: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none resize-none"
                    data-testid="edit-field-scholarship_info" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">UK Friendly</label>
                  <select value={editForm.foreign_friendly}
                    onChange={e => setEditForm(p => ({ ...p, foreign_friendly: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none bg-white"
                    data-testid="edit-field-foreign_friendly">
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>

              {/* Image preview */}
              {editForm.image_url && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Image Preview</label>
                  <img src={editForm.image_url} alt="preview" className="h-24 rounded-lg object-cover border border-slate-200"
                    onError={e => { e.target.style.display = "none"; }} />
                </div>
              )}

              {/* Logo upload */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Upload Logo / Image</label>
                <p className="text-xs text-slate-400 mb-2">Upload PNG or JPG from the college website. This replaces the placeholder image.</p>
                <label className="flex items-center gap-2 w-fit px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold uppercase tracking-wide rounded-lg cursor-pointer transition-all">
                  <Upload size={14} />
                  Choose Image File
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !editingCollege) return;
                      const formData = new FormData();
                      formData.append("file", file);
                      try {
                        const token = localStorage.getItem("cb_admin_token");
                        const res = await fetch(`${API}/api/admin/staging/colleges/${editingCollege.id}/upload-image`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData,
                        });
                        const data = await res.json();
                        if (data.image_url) setEditForm(p => ({ ...p, image_url: data.image_url }));
                      } catch {}
                      e.target.value = "";
                    }}
                  />
                </label>
                {editForm.image_url?.startsWith("https://") && editForm.image_url.includes("supabase") && (
                  <p className="text-xs text-green-600 font-semibold mt-1">Custom logo uploaded</p>
                )}
              </div>

              {/* Coaches */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  {editGender === "womens" ? "Women's" : "Men's"} Coaches
                </label>
                {(editForm.coaches || []).map((coach, ci) => (
                  <div key={ci} className="grid grid-cols-2 gap-2 p-3 border border-slate-100 rounded-lg mb-2 bg-slate-50">
                    <input placeholder="Name" value={coach.name || ""}
                      onChange={e => { const c=[...editForm.coaches]; c[ci]={...c[ci],name:e.target.value}; setEditForm(p=>({...p,coaches:c})); }}
                      className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-orange-400 outline-none" />
                    <input placeholder="Title" value={coach.title || ""}
                      onChange={e => { const c=[...editForm.coaches]; c[ci]={...c[ci],title:e.target.value}; setEditForm(p=>({...p,coaches:c})); }}
                      className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-orange-400 outline-none" />
                    <input placeholder="Email" value={coach.email || ""}
                      onChange={e => { const c=[...editForm.coaches]; c[ci]={...c[ci],email:e.target.value}; setEditForm(p=>({...p,coaches:c})); }}
                      className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-orange-400 outline-none" />
                    <input placeholder="Phone" value={coach.phone || ""}
                      onChange={e => { const c=[...editForm.coaches]; c[ci]={...c[ci],phone:e.target.value}; setEditForm(p=>({...p,coaches:c})); }}
                      className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-orange-400 outline-none" />
                    <div className="col-span-2 flex items-center gap-2">
                      <label className="text-xs text-slate-500 font-semibold w-28 flex-shrink-0">Last Verified:</label>
                      <input type="date" value={coach.last_verified || ""}
                        onChange={e => { const c=[...editForm.coaches]; c[ci]={...c[ci],last_verified:e.target.value}; setEditForm(p=>({...p,coaches:c})); }}
                        className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-orange-400 outline-none flex-1" />
                      <button type="button"
                        onClick={() => { const c=[...editForm.coaches]; c[ci]={...c[ci],last_verified:new Date().toISOString().slice(0,10)}; setEditForm(p=>({...p,coaches:c})); }}
                        className="text-xs text-green-600 font-semibold hover:text-green-800 whitespace-nowrap">Mark Today</button>
                    </div>
                    <button onClick={() => setEditForm(p => ({ ...p, coaches: p.coaches.filter((_,ii) => ii!==ci) }))}
                      className="col-span-2 text-xs text-red-500 hover:text-red-700 text-left font-semibold">Remove coach</button>
                  </div>
                ))}
                <button
                  onClick={() => setEditForm(p => ({ ...p, coaches: [...(p.coaches||[]), {name:"",title:"Head Coach",email:"",phone:""}] }))}
                  className="text-xs text-orange-600 hover:text-orange-800 font-bold">+ Add Coach</button>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => saveEdit(false)} disabled={savingEdit}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-black text-sm uppercase tracking-wider rounded-xl py-3 transition-all disabled:opacity-60"
                  data-testid="save-edit-btn">
                  {savingEdit ? "Saving…" : "Save Changes"}
                </button>
                <button onClick={() => saveEdit(true)} disabled={savingEdit}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-sm uppercase tracking-wider rounded-xl py-3 transition-all disabled:opacity-60"
                  data-testid="save-and-promote-btn">
                  Save &amp; Go Live
                </button>
                <button onClick={() => setEditingCollege(null)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm uppercase tracking-wider rounded-xl py-3 transition-all">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Promote to Live confirmation modal ──────────────────────── */}
      {showPromoteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle size={20} className="text-emerald-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Promote to Live</h3>
                <p className="text-xs text-slate-500">This action writes to the production coaches table</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-700 space-y-1">
              <p><strong>{promotePreview?.total}</strong> coaches eligible for promotion will be upserted into the live <code className="text-xs bg-slate-200 px-1 rounded">coaches</code> table ({promotePreview?.mens}M / {promotePreview?.womens}W).</p>
              <p className="text-xs text-slate-500 mt-2">
                Eligible: <strong>human_verified = true</strong> OR <strong>confidence ≥ 70</strong>. Soft-deleted coaches are excluded.
                Existing live records are updated; new records are inserted.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handlePromote}
                disabled={promoting}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-50"
                data-testid="confirm-promote-btn">
                {promoting ? "Promoting…" : "Yes, promote to live"}
              </button>
              <button
                onClick={() => setShowPromoteModal(false)}
                className="flex-1 py-2.5 border text-sm font-semibold rounded-xl hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
